import type {
  EventSummary,
  GuestAccessLifecycleRecord,
  GuestIdentityContext,
  GuestJourneySummaryRecord,
  GuestLifecycleRecord,
  GuestOnboardingSummaryRecord,
  GuestJourneySessionContext,
  GuestTaskRecord,
  GuestVisibleFormRecord,
  OccasionSummary,
  ResolvedUnifiedContextRecord,
  ResolvedLocalizationContext,
} from '@tov-plus/domain';

import type {
  GuestExperienceBootstrap,
  GuestOccasionContext,
  GuestIntelligenceSnapshot,
  GuestIntelligenceSummary,
  GuestRecentCommunication,
} from '../api/guestApi';
import { msg } from '@tov-plus/ui';

export interface GuestModuleSummary {
  id: string;
  label: string;
  summary: string;
}

export interface GuestExperience {
  occasion: OccasionSummary;
  guestDisplayName: string;
  openingExperience: GuestExperienceBootstrap['openingExperience'];
  accessMode: 'guest_token' | 'claimed_guest' | 'public_visitor';
  identitySummary: GuestIdentityContext;
  unifiedContext: ResolvedUnifiedContextRecord;
  designTheme?: GuestExperienceBootstrap['designTheme'];
  eventVisualOverrides: GuestExperienceBootstrap['eventVisualOverrides'];
  tokenInheritance: GuestExperienceBootstrap['tokenInheritance'];
  guestWebsite: GuestExperienceBootstrap['guestWebsite'];
  localizationContext: ResolvedLocalizationContext;
  multiLanguageCapability: GuestOccasionContext['multiLanguageCapability'];
  events: EventSummary[];
  forms: GuestVisibleFormRecord[];
  journeySummary: GuestJourneySummaryRecord;
  guestSession: GuestJourneySessionContext;
  onboardingSummary: GuestOnboardingSummaryRecord;
  accessLifecycle: GuestAccessLifecycleRecord;
  guestStateMachine: GuestLifecycleRecord;
  guestIntelligence: GuestIntelligenceSummary | null;
  guestIntelligenceSnapshots: GuestIntelligenceSnapshot[];
  tasks: GuestTaskRecord[];
  recentCommunications: GuestRecentCommunication[];
  modules: GuestModuleSummary[];
  notices: string[];
}

const baseModules: GuestModuleSummary[] = [
  {
    id: 'rsvp',
    label: msg('general.modules.rsvp.name'),
    summary: msg(
      'lib.experience.guest_experience.confirm_the_events_you_can_attend_from_one_occasion_first_flow',
    ),
  },
  {
    id: 'forms',
    label: msg('general.modules.forms.name'),
    summary: msg(
      'lib.experience.guest_experience.complete_guest_questions_when_the_planner_has_enabled_them',
    ),
  },
  {
    id: 'gallery',
    label: msg('general.modules.gallery.name'),
    summary: msg(
      'lib.experience.guest_experience.see_approved_memories_and_submit_guest_uploads_when_enabled',
    ),
  },
  {
    id: 'timeline',
    label: msg('general.modules.schedule.name'),
    summary: msg(
      'lib.experience.guest_experience.follow_the_guest_safe_schedule_summary_for_visible_events',
    ),
  },
  {
    id: 'seating',
    label: msg('general.modules.seating.name'),
    summary: msg(
      'lib.experience.guest_experience.check_your_published_table_and_seat_assignment_when_available',
    ),
  },
];

export async function buildGuestExperience(
  context: GuestOccasionContext,
  openingExperience: GuestExperienceBootstrap['openingExperience'],
  events: EventSummary[],
  forms: GuestVisibleFormRecord[],
  journeySummary: GuestJourneySummaryRecord,
  guestSession: GuestJourneySessionContext,
  onboardingSummary: GuestOnboardingSummaryRecord,
  accessLifecycle: GuestAccessLifecycleRecord,
  guestStateMachine: GuestLifecycleRecord,
  guestIntelligence: GuestIntelligenceSummary | null,
  guestIntelligenceSnapshots: GuestIntelligenceSnapshot[],
  tasks: GuestTaskRecord[],
  recentCommunications: GuestRecentCommunication[],
  designTheme: GuestExperienceBootstrap['designTheme'],
  eventVisualOverrides: GuestExperienceBootstrap['eventVisualOverrides'],
  tokenInheritance: GuestExperienceBootstrap['tokenInheritance'],
  guestWebsite: GuestExperienceBootstrap['guestWebsite'],
): Promise<GuestExperience> {
  await Promise.resolve();

  const availableModuleKeys = new Set(
    context.unifiedContext.moduleScope
      .filter((item) => item.audienceVisible)
      .map((item) => item.moduleKey),
  );

  return {
    occasion: context.occasion,
    guestDisplayName: context.guestDisplayName,
    openingExperience,
    accessMode: context.accessMode,
    identitySummary: context.identitySummary,
    unifiedContext: context.unifiedContext,
    designTheme,
    eventVisualOverrides,
    tokenInheritance,
    guestWebsite,
    localizationContext: context.localizationContext,
    multiLanguageCapability: context.multiLanguageCapability,
    events,
    forms,
    journeySummary,
    guestSession,
    onboardingSummary,
    accessLifecycle,
    guestStateMachine,
    guestIntelligence,
    guestIntelligenceSnapshots,
    tasks,
    recentCommunications,
    modules: baseModules.filter((module) => {
      if (module.id === 'timeline') {
        return availableModuleKeys.has('timeline');
      }

      return module.id === 'gallery' || module.id === 'seating'
        ? availableModuleKeys.has(module.id)
        : true;
    }),
    notices: [
      'This guest shell resolves its occasion context from the backend guest seam.',
      `Resolved locale: ${context.localizationContext.resolvedLocaleCode}.`,
      `Journey phase: ${journeySummary.phase}.`,
      `Lifecycle state: ${guestStateMachine.currentState}.`,
      `Response scope: ${context.identitySummary.responseScope}.`,
      context.multiLanguageCapability.enabled
        ? msg(
            'lib.experience.guest_experience.multi_language_mode_is_enabled_for_this_occasion',
          )
        : msg(
            'lib.experience.guest_experience.this_occasion_is_using_single_default_locale_mode',
          ),
      `${forms.length} form flow${forms.length === 1 ? '' : 's'} currently visible for this guest context.`,
    ],
  };
}
